import {
  EuiButton,
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
} from "@inscopix/ideas-eui";
import { useState } from "react";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { useProjectFolderContext } from "../folders/ProjectFoldersProvider";
import { HexColor } from "vega";
import { FieldFolderName } from "./fields/FieldFolderName";
import { FieldFolderColor } from "./fields/FieldFolderColor";
import { ProjectFolderDjango } from "../folders/ProjectFoldersProvider.api";

type Fields = {
  name: {
    value: string;
    isValid: boolean;
  };
  color: {
    value: HexColor | null;
    isValid: boolean;
  };
};

interface ModalEditFolderProps {
  folder: ProjectFolderDjango;
  onClose: () => void;
}

/** Component that renders a modal for editing an existing folder */
export const ModalEditFolder = ({ folder, onClose }: ModalEditFolderProps) => {
  const editFolder = useProjectFolderContext((s) => s.editFolder);
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState<Fields>({
    name: {
      value: folder.name,
      isValid: folder.name.length > 0,
    },
    color: {
      value: folder.color,
      isValid: true,
    },
  });

  const handleFieldChange = <T extends keyof Fields>(
    fieldName: T,
    field: Fields[T],
  ) => {
    setFields((prevFields) => ({
      ...prevFields,
      [fieldName]: field,
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const { error } = await editFolder({
      folderId: folder.id,
      name: fields.name.value.trim(),
      color: fields.color.value,
    });
    setIsLoading(false);

    if (error !== undefined) {
      addUtilityToastFailure("Failed to create folder");
    }

    onClose();
  };

  return (
    <EuiModal onClose={onClose}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>Edit Folder</EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody>
        <EuiForm
          id={`create-folder-form`}
          component="form"
          role="form"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          <EuiFlexGroup>
            <EuiFlexItem>
              <FieldFolderName
                autoFocus={true}
                value={fields.name.value}
                color={fields.color.value}
                colorIsValid={fields.color.isValid}
                onChange={(field) => handleFieldChange("name", field)}
              />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <FieldFolderColor
                value={fields.color.value}
                isValid={fields.color.isValid}
                onChange={(field) => handleFieldChange("color", field)}
              />
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiForm>
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>Cancel</EuiButtonEmpty>
        <EuiButton
          isLoading={isLoading}
          fill
          onClick={() => void handleSubmit()}
          disabled={Object.values(fields).some((field) => !field.isValid)}
        >
          Update
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
